import React, { useMemo, useState } from 'react';

import { Heading } from '~/components/core';
import Text from '~/components/core/TextComponents/Text';
import FileDropZone from '~/components/FileDropZone';

import { useSopConfiguration } from './hooks/useSopConfiguration';
import type { SopUploadStepProps } from './types';

const SopUploadStep: React.FC<SopUploadStepProps> = ({ onNext, onUploadingChange, isUploading, uploadedFile }) => {
  const [uploadPercent, setUploadPercent] = useState(uploadedFile ? 100 : 0);
  const [error, setError] = useState<string | null>(null);

  const { uploadFile } = useSopConfiguration();

  // Create a mock File object for display purposes when we have persisted upload data
  const displayFile = useMemo(() => {
    if (uploadedFile) {
      // Create a minimal file-like object for FileDropZone display
      return new File([], uploadedFile.file_name, { type: 'application/octet-stream' });
    }
    return undefined;
  }, [uploadedFile]);

  const [file, setFile] = useState<File | undefined>(displayFile);

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setError(null);
    onUploadingChange(true);

    try {
      const result = await uploadFile(selectedFile, (progress: ProgressEvent) => {
        const percent = (progress.loaded / progress.total) * 100;
        setUploadPercent(percent);
      });

      // Notify parent dialog with upload result
      onNext({
        stored_file_id: result.stored_file_id,
        storage_filename: result.storage_filename,
        file_name: selectedFile.name,
      });
    } catch (err) {
      setError('Failed to upload file. Please try again.');
    } finally {
      onUploadingChange(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center gap-16">
      <div>
        <Heading variant={Heading.TYPES.H2} colorVariant={Heading.COLOR_VARIANTS.PRIMARY}>
          Upload Your SOP File
        </Heading>
        <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          Upload an SOP file (PDF, DOCX, or DOC). You may upload multiple SOP files if your procedure is split into
          sections. Once uploaded, the files will appear in your file list. You can remove or replace any file before
          continuing.
        </Text>
      </div>

      <div className="flex items-center justify-center">
        <FileDropZone
          uploadPercent={uploadPercent}
          error={!!error}
          errorMessage={error || undefined}
          file={file}
          onFileSelect={handleFileSelect}
          onCancelFileSelect={() => {
            setFile(undefined);
            setUploadPercent(0);
            setError(null);
          }}
          dropZoneOptions={{
            accept: '.pdf,.doc,.docx',
            maxSize: 50 * 1024 * 1024, // 50MB
          }}
          baseStyleOverride={{
            height: '400px',
            width: '600px',
            overflow: 'hidden',
            wordBreak: 'break-all',
          }}
          disabled={isUploading}
        />
      </div>
    </div>
  );
};

export default SopUploadStep;
